import { ampli } from '@/ampli';
import { useEffect, useState } from 'react';

import { TokenLaunchpadBadge } from '@/components/Badges/TokenLaunchpadBadge';
import { LiquidityScoreModal } from '@/components/Modal/LiquidityScoreModal';
import { SecurityModal } from '@/components/Modal/SecurityModuleModal';
import { LiquidityScore } from '@/components/TokenProfile/TokenLiquidityScore';
import { SecurityAuditPreview } from '@/components/TokenProfile/TokenProfileSecurityAudit/SecurityAuditPreview';

import { isLaunchpadSupported, launchpadDefinitions } from '@/constants/launchpads';

import { useSecurityAssessment } from '@/hooks/useSecurityAssessment';
import { useTokenInfo } from '@/hooks/useTokenInfo';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';
import { isZoraCreatorCoin, isZoraToken, type ZoraType } from '@/utils/zora';

type TokenPanelStatsProps = {
  token: TokenResult;
};

const reportedLaunchpads = new Set<string>();

/**
 * Qualitative token chips (launchpad, liquidity score, security audit) for the homepage
 * panel; the numeric stats live in `TokenPanelStatCells`. Unlike the token page there is
 * no section to scroll to, so the score badges open their detail modals instead.
 */
export const TokenPanelStats = ({ token }: TokenPanelStatsProps) => {
  const { data: tokenInfo } = useTokenInfo(token.address, token.chainId);
  const { level, risks, highRisks } = useSecurityAssessment({ activeToken: token });
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);
  const [isLiquidityOpen, setIsLiquidityOpen] = useState(false);
  const [liquidityScore, setLiquidityScore] = useState<number>();

  const showSecurityAudit = level !== 'Pending' && risks + highRisks > 0;
  const launchpadName = token.launchpad?.name ?? tokenInfo?.launchpad;

  // Report gaps in launchpad definitions and Token API attribution once per token and launchpad.
  useEffect(() => {
    const tokenAddress = normalizeTokenAddress(token.address, token.chainId);
    const codexLaunchpad = tokenInfo?.launchpad;

    const key = `${tokenAddress}:${token.chainId}:${codexLaunchpad}`;
    if (!codexLaunchpad || reportedLaunchpads.has(key)) return;

    const properties = {
      launchpad_id: codexLaunchpad,
      token_address: tokenAddress,
      token_chain_id: token.chainId,
      token_symbol: token.symbol,
    };

    if (!isLaunchpadSupported(codexLaunchpad)) {
      ampli.tokenLaunchpadDefinitionMissing(properties);
    } else if (launchpadDefinitions[codexLaunchpad].tokenApiSupport && !token.launchpad?.name) {
      ampli.tokenLaunchpadMissingFromTokenApi(properties);
    } else {
      return;
    }

    reportedLaunchpads.add(key);
  }, [tokenInfo?.launchpad, token]);

  let zoraType: ZoraType;
  if (isZoraToken(token)) {
    zoraType = isZoraCreatorCoin(token) ? 'creator' : 'post';
  }

  const handleLiquidityClick = (score: number | undefined) => {
    setLiquidityScore(score);
    setIsLiquidityOpen(true);
  };

  return (
    <>
      {launchpadName && <TokenLaunchpadBadge id={launchpadName} />}
      <LiquidityScore activeToken={token} zoraType={zoraType} onClick={handleLiquidityClick} />
      {showSecurityAudit && (
        <SecurityAuditPreview activeToken={token} onClick={() => setIsSecurityOpen(true)} />
      )}
      <SecurityModal activeToken={token} isOpen={isSecurityOpen} onOpenChange={setIsSecurityOpen} />
      <LiquidityScoreModal
        activeToken={token}
        currentScore={liquidityScore}
        isOpen={isLiquidityOpen}
        onOpenChange={setIsLiquidityOpen}
      />
    </>
  );
};
